/**
 * useCountries — fetch the list of supported countries from the catalog.
 *
 * Cached effectively-forever (24h staleTime + 7d gcTime) — countries
 * never change at app runtime. Pairs with useCities() for the picker
 * UI in edit-profile. ISO2 code (`code` column) is exposed as `iso2`
 * on the returned shape for convenience.
 */
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../supabase'
import type { Cols } from '../types/db-rows'

export interface CountryOption {
  id: string
  iso2: string
  name: string
}

const countryKeys = {
  all: ['countries'] as const,
  list: () => [...countryKeys.all, 'list'] as const,
}

export function useCountries() {
  return useQuery({
    queryKey: countryKeys.list(),
    queryFn: async (): Promise<CountryOption[]> => {
      const { data, error } = await supabase
        .from('countries')
        .select('id, code, name')
        .eq('is_active', true)
        .order('name', { ascending: true })

      if (error) throw error

      const rows: Cols<'countries', 'id' | 'code' | 'name'>[] = data ?? []
      return rows.map((row) => ({
        id: row.id,
        iso2: row.code,
        name: row.name,
      }))
    },
    staleTime: 1000 * 60 * 60 * 24, // 24h
    gcTime: 1000 * 60 * 60 * 24 * 7, // 7d
  })
}
